import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import {
  HeadsetMic as HeadsetMicIcon,
  Voicemail as VoicemailIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CheckCircle as ResolvedIcon,
  RadioButtonUnchecked as UnresolvedIcon,
  ContentCopy as CopyIcon,
  MarkEmailRead as MarkReadIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Label as TagIcon,
  FiberManualRecord as DotIcon,
  AddCircleOutline as AddFunnelIcon,
  Cancel as RemoveFunnelIcon,
} from '@mui/icons-material';
import { SupportInteraction, EmailAccountStatus, SupportTag, SupportFunnel } from './types';
import { apiService } from '../../services/api';

const DEEP_TEAL = '#1A5F7A';
const WARM_GOLD = '#D4AC0D';

interface MessageDetailProps {
  selectedItem: SupportInteraction | null;
  accounts: EmailAccountStatus[];
  onReply: (body: string, accountId?: string) => Promise<void>;
  onResolve: (notes: string, tags: string[]) => Promise<void>;
  onUnresolve: () => Promise<void>;
  onCallback: () => Promise<void>;
  onMarkRead: () => void;
  /** Called when tags are saved so parent can refresh the item */
  onTagsUpdated?: (externalId: string, tags: string[]) => void;
}

const MessageDetail: React.FC<MessageDetailProps> = ({
  selectedItem,
  accounts,
  onReply,
  onResolve,
  onUnresolve,
  onCallback,
  onMarkRead,
  onTagsUpdated,
}) => {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyAccountId, setReplyAccountId] = useState<string>('');
  const [replySending, setReplySending] = useState(false);

  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveTags, setResolveTags] = useState('');

  // Tags editor state
  const [availableTags, setAvailableTags] = useState<SupportTag[]>([]);
  const [currentTags, setCurrentTags] = useState<string[]>([]);
  const [tagsSaving, setTagsSaving] = useState(false);

  // Funnel state
  const [funnels, setFunnels] = useState<SupportFunnel[]>([]);
  const [addFunnelOpen, setAddFunnelOpen] = useState(false);
  const [selectedFunnelId, setSelectedFunnelId] = useState('');
  const [selectedStageId, setSelectedStageId] = useState('');
  const [funnelSaving, setFunnelSaving] = useState(false);
  // Map externalId → { funnelId, stageName, funnelName }
  const [funnelAssignments, setFunnelAssignments] = useState<Record<string, { funnelId: string; funnelName: string; stageName: string; stageId: string }>>({});

  const loadSidePanelData = useCallback(async () => {
    try {
      const [tags, funnelList] = await Promise.all([
        apiService.getSupportTags(),
        apiService.getSupportFunnels(),
      ]);
      setAvailableTags(tags);
      setFunnels(funnelList);
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    loadSidePanelData();
  }, [loadSidePanelData]);

  // Reset reply state when switching messages
  useEffect(() => {
    setReplyOpen(false);
    setReplyBody('');
    setResolveOpen(false);
    if (selectedItem?.email_account_id) {
      setReplyAccountId(selectedItem.email_account_id);
    } else if (accounts.length > 0) {
      setReplyAccountId(accounts[0].id);
    } else {
      setReplyAccountId('');
    }
    setCurrentTags(selectedItem?.tags ?? []);
    setAddFunnelOpen(false);
    setSelectedFunnelId('');
    setSelectedStageId('');
  }, [selectedItem?.external_id]);

  const handleSaveTags = async () => {
    if (!selectedItem) return;
    setTagsSaving(true);
    try {
      await apiService.updateInteractionTags(selectedItem.external_id, currentTags);
      onTagsUpdated?.(selectedItem.external_id, currentTags);
    } catch {
      // Silently ignore
    } finally {
      setTagsSaving(false);
    }
  };

  const handleAssignFunnel = async () => {
    if (!selectedItem || !selectedFunnelId || !selectedStageId) return;
    setFunnelSaving(true);
    try {
      await apiService.assignInteractionToFunnel(selectedItem.external_id, {
        funnelId: selectedFunnelId,
        stageId: selectedStageId,
      });
      const funnel = funnels.find(f => f.id === selectedFunnelId);
      const stage = funnel?.stages.find(s => s.id === selectedStageId);
      if (funnel && stage) {
        setFunnelAssignments(prev => ({
          ...prev,
          [`${selectedItem.external_id}:${selectedFunnelId}`]: {
            funnelId: selectedFunnelId,
            funnelName: funnel.name,
            stageName: stage.name,
            stageId: selectedStageId,
          },
        }));
      }
      setAddFunnelOpen(false);
      setSelectedFunnelId('');
      setSelectedStageId('');
    } catch {
      // Silently ignore
    } finally {
      setFunnelSaving(false);
    }
  };

  const handleRemoveFunnel = async (funnelId: string) => {
    if (!selectedItem) return;
    try {
      await apiService.removeInteractionFromFunnel(selectedItem.external_id, funnelId);
      setFunnelAssignments(prev => {
        const next = { ...prev };
        delete next[`${selectedItem.external_id}:${funnelId}`];
        return next;
      });
    } catch {
      // Silently ignore
    }
  };

  const currentAssignments = selectedItem
    ? Object.entries(funnelAssignments)
        .filter(([k]) => k.startsWith(selectedItem.external_id + ':'))
        .map(([, v]) => v)
    : [];

  const handleSendReply = async () => {
    if (!replyBody.trim()) return;
    setReplySending(true);
    try {
      await onReply(replyBody, replyAccountId || undefined);
      setReplyBody('');
      setReplyOpen(false);
    } finally {
      setReplySending(false);
    }
  };

  const handleResolve = async () => {
    const tags = resolveTags.split(',').map(t => t.trim()).filter(Boolean);
    await onResolve(resolveNotes, tags);
    setResolveOpen(false);
    setResolveNotes('');
    setResolveTags('');
  };

  const copyPhone = (phone: string) => {
    navigator.clipboard.writeText(phone);
  };

  // Find account color
  const accountColor = selectedItem?.email_account_id
    ? accounts.find(a => a.id === selectedItem.email_account_id)?.color
    : undefined;
  const accountName = selectedItem?.email_account_id
    ? accounts.find(a => a.id === selectedItem.email_account_id)?.accountName
    : undefined;

  if (!selectedItem) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <HeadsetMicIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography color="text.secondary">Select a message to view details</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {selectedItem.type === 'voicemail' ? (
              <VoicemailIcon color="warning" />
            ) : (
              <EmailIcon color="info" />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedItem.subject || '(No subject)'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>From:</strong> {selectedItem.from_address}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <TimeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              {new Date(selectedItem.received_at).toLocaleString()}
            </Typography>
            {accountColor && (
              <Chip
                icon={<DotIcon sx={{ fontSize: '12px !important', color: `${accountColor} !important` }} />}
                label={accountName || 'Account'}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          {selectedItem.member_name && (
            <Chip
              icon={<PersonIcon />}
              label={`LifeScribe Member: ${selectedItem.member_name} (${selectedItem.member_email})`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {selectedItem.is_resolved ? (
            <Tooltip title="Reopen">
              <IconButton onClick={onUnresolve} color="success" size="small">
                <ResolvedIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Mark as resolved">
              <IconButton onClick={() => setResolveOpen(true)} color="default" size="small">
                <UnresolvedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Status chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          label={selectedItem.type === 'voicemail' ? 'Voicemail' : 'Email'}
          size="small"
          color={selectedItem.type === 'voicemail' ? 'warning' : 'info'}
        />
        <Chip label={selectedItem.is_read ? 'Read' : 'Unread'} size="small" variant="outlined" />
        <Chip
          label={selectedItem.is_resolved ? 'Resolved' : 'Open'}
          size="small"
          color={selectedItem.is_resolved ? 'success' : 'error'}
        />
        {selectedItem.direction === 'outbound' && (
          <Chip label="Sent" size="small" color="default" />
        )}
        {selectedItem.tags?.map(tag => (
          <Chip key={tag} label={tag} size="small" variant="outlined" icon={<TagIcon />} />
        ))}
      </Box>

      {selectedItem.resolution_notes && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Resolution notes:</strong> {selectedItem.resolution_notes}
        </Alert>
      )}

      {/* ── Tags editor (always visible) ───────────────────────────────── */}
      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: `${DEEP_TEAL}04` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TagIcon sx={{ fontSize: 14, color: DEEP_TEAL }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: DEEP_TEAL, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Tags
          </Typography>
        </Box>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={availableTags.map(t => t.name)}
          value={currentTags}
          onChange={(_, newValue) => setCurrentTags(newValue as string[])}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const tagColor = availableTags.find(t => t.name === option)?.color;
              return (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{ bgcolor: tagColor ? `${tagColor}22` : undefined, borderColor: tagColor, color: tagColor ? tagColor : undefined, fontSize: '0.7rem', height: 22 }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField {...params} placeholder="Add tags…" variant="outlined" size="small" />
          )}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSaveTags}
            disabled={tagsSaving}
            sx={{ textTransform: 'none', borderColor: DEEP_TEAL, color: DEEP_TEAL, fontSize: '0.75rem' }}
          >
            {tagsSaving ? <CircularProgress size={12} /> : 'Save tags'}
          </Button>
        </Box>
      </Box>

      {/* ── Funnel assignments ─────────────────────────────────────────── */}
      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: `${WARM_GOLD}05` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddFunnelIcon sx={{ fontSize: 14, color: WARM_GOLD }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#8a6a00', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Support Funnels
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<AddFunnelIcon sx={{ fontSize: 12 }} />}
            onClick={() => setAddFunnelOpen(true)}
            sx={{ textTransform: 'none', fontSize: '0.7rem', color: WARM_GOLD, borderColor: WARM_GOLD, py: 0.25, px: 0.75 }}
            variant="outlined"
          >
            Add
          </Button>
        </Box>
        {currentAssignments.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Not in any funnel
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {currentAssignments.map(a => (
              <Chip
                key={a.funnelId}
                label={`${a.funnelName} → ${a.stageName}`}
                size="small"
                onDelete={() => handleRemoveFunnel(a.funnelId)}
                deleteIcon={<RemoveFunnelIcon sx={{ fontSize: '14px !important' }} />}
                sx={{ bgcolor: `${WARM_GOLD}18`, borderColor: WARM_GOLD, color: '#8a6a00', fontSize: '0.7rem', height: 22 }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Voicemail specific */}
      {selectedItem.type === 'voicemail' && (
        <Box sx={{ mb: 2 }}>
          {selectedItem.caller_phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Phone:</strong> {selectedItem.caller_phone}
              </Typography>
              <Tooltip title="Copy phone number">
                <IconButton size="small" onClick={() => copyPhone(selectedItem.caller_phone!)}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={<PhoneIcon />}
                onClick={onCallback}
                sx={{ ml: 1, bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
              >
                Call Back
              </Button>
            </Box>
          )}
          {selectedItem.voicemail_transcript && (
            <Card variant="outlined" sx={{ bgcolor: 'grey.50', p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Voicemail Transcript
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedItem.voicemail_transcript}
              </Typography>
            </Card>
          )}
        </Box>
      )}

      {/* Email body */}
      <Box sx={{ mb: 2, flex: 1 }}>
        {selectedItem.body_html ? (
          <Box
            sx={{
              '& img': { maxWidth: '100%' },
              '& a': { color: 'primary.main' },
              fontSize: '0.875rem',
              lineHeight: 1.6,
            }}
            dangerouslySetInnerHTML={{ __html: selectedItem.body_html }}
          />
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {selectedItem.body_text || '(No content)'}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => setReplyOpen(true)}
          sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
        >
          Reply
        </Button>
        {!selectedItem.is_resolved && (
          <Button
            variant="outlined"
            startIcon={<ResolvedIcon />}
            onClick={() => setResolveOpen(true)}
            color="success"
          >
            Resolve
          </Button>
        )}
        {!selectedItem.is_read && (
          <Button
            variant="outlined"
            startIcon={<MarkReadIcon />}
            onClick={onMarkRead}
            size="small"
          >
            Mark Read
          </Button>
        )}
      </Box>

      {/* Inline Reply */}
      {replyOpen && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Reply to: {selectedItem.from_address}</Typography>
            <IconButton size="small" onClick={() => setReplyOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Account selector (if multiple accounts) */}
          {accounts.length > 1 && (
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Send from</InputLabel>
              <Select
                value={replyAccountId}
                label="Send from"
                onChange={(e) => setReplyAccountId(e.target.value)}
              >
                {accounts.map(acc => (
                  <MenuItem key={acc.id} value={acc.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DotIcon sx={{ fontSize: 12, color: acc.color }} />
                      {acc.accountName || acc.email} ({acc.email})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Type your reply..."
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="text" onClick={() => setReplyOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={replySending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSendReply}
              disabled={replySending || !replyBody.trim()}
              sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
            >
              Send Reply
            </Button>
          </Box>
        </Box>
      )}

      {/* Add to Funnel Dialog */}
      <Dialog open={addFunnelOpen} onClose={() => setAddFunnelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', color: DEEP_TEAL }}>
          Add to Support Funnel
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Funnel</InputLabel>
            <Select
              value={selectedFunnelId}
              label="Funnel"
              onChange={(e) => { setSelectedFunnelId(e.target.value); setSelectedStageId(''); }}
            >
              {funnels.filter(f => f.is_active).map(f => (
                <MenuItem key={f.id} value={f.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DotIcon sx={{ fontSize: 12, color: f.color }} />
                    {f.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedFunnelId && (
            <FormControl fullWidth size="small">
              <InputLabel>Stage</InputLabel>
              <Select
                value={selectedStageId}
                label="Stage"
                onChange={(e) => setSelectedStageId(e.target.value)}
              >
                {funnels.find(f => f.id === selectedFunnelId)?.stages.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFunnelOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignFunnel}
            disabled={!selectedFunnelId || !selectedStageId || funnelSaving}
            sx={{ bgcolor: WARM_GOLD, color: '#000', '&:hover': { bgcolor: '#b8960a' } }}
          >
            {funnelSaving ? <CircularProgress size={16} /> : 'Add to Funnel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={resolveOpen} onClose={() => setResolveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Interaction</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Resolution Notes"
            placeholder="What was done to resolve this..."
            value={resolveNotes}
            onChange={(e) => setResolveNotes(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Tags (comma-separated)"
            placeholder="billing, technical, account, feature-request"
            value={resolveTags}
            onChange={(e) => setResolveTags(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResolve} color="success" startIcon={<ResolvedIcon />}>
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageDetail;
